/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { ConversationSearchCriteria, ConversationFilterState, ConversationLocationState, ViewMode } from '../store';
import { useSessionUpdateZustandStore } from '../modules/store/sessionUpdateZustandStore';

export interface SessionUpdateParams {
  searchCriteria?: ConversationSearchCriteria;
  filterState?: ConversationFilterState;
  locationState?: ConversationLocationState;
  viewMode?: ViewMode;
}

export interface SessionUpdateResult {
  success: boolean;
  sessionData?: any;
  error?: string;
}

const formatDateForSession = (isoDate: string | undefined): string => {
  if (!isoDate) return '';
  const normalized = (isoDate || '').slice(0, 10);
  const [year, month, day] = normalized.split('-');
  if (year && month && day) return `${month}/${day}/${year}`;
  return isoDate;
};

const calculateLengthOfStay = (checkInDate?: string, checkOutDate?: string): number => {
  if (!checkInDate || !checkOutDate) return 1;
  const msPerDay = 1000 * 60 * 60 * 24;
  const diff = Math.round((new Date(checkOutDate).getTime() - new Date(checkInDate).getTime()) / msPerDay);
  return Math.max(diff, 1);
};

/**
 * Formats date from ISO format (YYYY-MM-DD) to t-start/t-end format (YYYY-MM-DD)
 */
const formatDateForTParam = (isoDate: string | undefined): string => {
  if (!isoDate) return '';
  return (isoDate || '').slice(0, 10);
};

/**
 * Builds URL query parameters for the /validate/findHotels API call.
 * This follows the same pattern as the shop component's submitSearchRequestHelper.
 *
 * Reference URL format from shop:
 * /mi/validate/findHotels?destinationAddress.destination=asia&destinationAddress.region=asia
 * &searchType=InCity&amenities=adult-only-pool&transportation=parking&deviceType=desktop-web
 * &view=list&isInternalSearch=true&vsInitialRequest=false&for-hotels-nearme=Near
 * &collapseAccordian=is-hidden&singleSearch=true&singleSearchAutoSuggest=Unmatched
 * &flexibleDateSearchRateDisplay=false&isSearch=true&isRateCalendar=true&recordsPerPage=40
 * &destinationAddress.location=asia&searchRadius=50&isTransient=true&initialRequest=true
 * &fromToDate=04/29/2026&fromToDate_submit=04/30/2026&fromDate=04/29/2026&toDate=04/30/2026
 * &toDateDefaultFormat=04/30/2026&fromDateDefaultFormat=04/29/2026&flexibleDateSearch=true
 * &isHideFlexibleDateCalendar=false&t-start=2026-04-29&t-end=2026-04-30
 * &isFlexibleDatesOptionSelected=true&lengthOfStay=1&roomCount=1&numAdultsPerRoom=1
 * &childrenCount=0&clusterCode=none&numberOfRooms=1
 */
export const buildValidateFindHotelsQueryParams = (params: SessionUpdateParams): string => {
  const { searchCriteria, filterState, locationState, viewMode } = params;
  const urlParams = new URLSearchParams();

  // Location/destination parameters (placed first to match shop URL order)
  if (locationState?.destination) {
    urlParams.set('destinationAddress.destination', locationState.destination);
  } else if (locationState?.city && locationState?.stateProvince) {
    urlParams.set('destinationAddress.destination', `${locationState.city}, ${locationState.stateProvince}`);
  } else if (locationState?.city) {
    urlParams.set('destinationAddress.destination', locationState.city);
  }

  if (locationState?.region) {
    urlParams.set('destinationAddress.region', locationState.region);
  }

  // Search type
  urlParams.set('searchType', 'InCity');

  // Filter parameters (placed early to match shop URL order)
  if (filterState?.amenities && filterState.amenities.length > 0) {
    urlParams.set('amenities', filterState.amenities.join(','));
  }
  if (filterState?.transportationTypes && filterState.transportationTypes.length > 0) {
    urlParams.set('transportation', filterState.transportationTypes.join(','));
  }
  if (filterState?.hotelTypes && filterState.hotelTypes.length > 0) {
    urlParams.set('hotelType', filterState.hotelTypes.join(','));
  }
  if (filterState?.activities && filterState.activities.length > 0) {
    urlParams.set('activities', filterState.activities.join(','));
  }
  if (filterState?.brands && filterState.brands.length > 0) {
    urlParams.set('marriottBrands', filterState.brands.join(','));
  }
  if (filterState?.events && filterState.events.length > 0) {
    urlParams.set('events', filterState.events.join(','));
  }

  // Device and view parameters
  urlParams.set('deviceType', 'desktop-web');
  urlParams.set('view', viewMode === 'map' ? 'map' : 'list');

  // Fixed search parameters (matching shop order)
  urlParams.set('isInternalSearch', 'true');
  urlParams.set('vsInitialRequest', 'false');
  urlParams.set('for-hotels-nearme', 'Near');
  urlParams.set('collapseAccordian', 'is-hidden');
  urlParams.set('singleSearch', 'true');
  urlParams.set('singleSearchAutoSuggest', 'Unmatched');
  urlParams.set('flexibleDateSearchRateDisplay', 'false');
  urlParams.set('isSearch', 'true');

  // Indicate this request is from the chat page
  urlParams.set('pageType', 'chat');

  // Date parameters
  const fromDate = formatDateForSession(searchCriteria?.checkInDate);
  const toDate = formatDateForSession(searchCriteria?.checkOutDate);
  const tStartDate = formatDateForTParam(searchCriteria?.checkInDate);
  const tEndDate = formatDateForTParam(searchCriteria?.checkOutDate);
  const lengthOfStay = calculateLengthOfStay(searchCriteria?.checkInDate, searchCriteria?.checkOutDate);
  const isFlexible = searchCriteria?.flexible ?? false;

  urlParams.set('isRateCalendar', isFlexible ? 'true' : 'false');
  urlParams.set('recordsPerPage', '40');

  // Additional location parameters
  if (locationState?.destination) {
    urlParams.set('destinationAddress.location', locationState.destination);
  } else if (locationState?.city) {
    urlParams.set('destinationAddress.location', locationState.city);
  }

  urlParams.set('searchRadius', '50');
  urlParams.set('isTransient', 'true');
  urlParams.set('initialRequest', 'true');

  // Date values in shop format
  if (fromDate) {
    urlParams.set('fromToDate', fromDate);
  }
  if (toDate) {
    urlParams.set('fromToDate_submit', toDate);
  }
  if (fromDate) {
    urlParams.set('fromDate', fromDate);
  }
  if (toDate) {
    urlParams.set('toDate', toDate);
  }
  if (toDate) {
    urlParams.set('toDateDefaultFormat', toDate);
  }
  if (fromDate) {
    urlParams.set('fromDateDefaultFormat', fromDate);
  }

  urlParams.set('flexibleDateSearch', isFlexible ? 'true' : 'false');
  urlParams.set('isHideFlexibleDateCalendar', 'false');

  // t-start and t-end use YYYY-MM-DD format
  if (tStartDate) {
    urlParams.set('t-start', tStartDate);
  }
  if (tEndDate) {
    urlParams.set('t-end', tEndDate);
  }

  urlParams.set('isFlexibleDatesOptionSelected', isFlexible ? 'true' : 'false');
  urlParams.set('lengthOfStay', String(lengthOfStay));

  // Room and guest parameters
  const roomCount = searchCriteria?.numRooms ?? 1;
  const numAdultsPerRoom = searchCriteria?.numAdultsPerRoom ?? 1;
  const childrenCount = searchCriteria?.numChildrenPerRoom ?? 0;
  const childrenAges = searchCriteria?.childrenAges ?? [];

  urlParams.set('roomCount', String(roomCount));
  urlParams.set('numAdultsPerRoom', String(numAdultsPerRoom));
  urlParams.set('childrenCount', String(childrenCount));

  if (childrenCount > 0 && childrenAges.length > 0) {
    urlParams.set('childrenAges', childrenAges.join(','));
  }

  // Rate parameters
  const useRewardsPoints = searchCriteria?.useRewardsPoints ?? false;
  const clusterCode = searchCriteria?.clusterCode;
  const corporateCode = searchCriteria?.corporateCode;
  const groupCode = searchCriteria?.groupCode;

  if (!useRewardsPoints) {
    if (corporateCode || clusterCode === 'corp') {
      urlParams.set('clusterCode', 'corp');
      if (corporateCode) urlParams.set('corporateCode', corporateCode);
    } else if (clusterCode === 'AAA' || clusterCode === 'aaa') {
      urlParams.set('clusterCode', 'aaa');
    } else if (clusterCode === 'GOV' || clusterCode === 'gov') {
      urlParams.set('clusterCode', 'gov');
    } else if (clusterCode === 'S9R' || clusterCode === 's9r') {
      urlParams.set('clusterCode', 'S9R');
    } else if (groupCode) {
      urlParams.set('clusterCode', 'grp');
      urlParams.set('groupCode', groupCode);
    } else {
      urlParams.set('clusterCode', 'none');
    }
  } else {
    urlParams.set('clusterCode', 'none');
  }

  urlParams.set('numberOfRooms', String(roomCount));

  // Additional location details
  if (locationState?.placeId) {
    urlParams.set('destinationAddress.placeId', locationState.placeId);
  }
  if (locationState?.latitude) {
    urlParams.set('destinationAddress.latitude', String(locationState.latitude));
  }
  if (locationState?.longitude) {
    urlParams.set('destinationAddress.longitude', String(locationState.longitude));
  }
  if (locationState?.city) {
    urlParams.set('destinationAddress.city', locationState.city);
    urlParams.set('destinationAddress.mainText', locationState.city);
  }
  if (locationState?.stateProvince) {
    urlParams.set('destinationAddress.stateProvince', locationState.stateProvince);
    urlParams.set('destinationAddress.stateProvinceDisplayName', locationState.stateProvince);
  }
  if (locationState?.country) {
    urlParams.set('destinationAddress.country', locationState.country);
    urlParams.set('countryName', locationState.country);
  }
  if (locationState?.address) {
    urlParams.set('destinationAddress.address', locationState.address);
  }
  if (locationState?.stateProvince && locationState?.country) {
    urlParams.set('destinationAddress.secondaryText', `${locationState.stateProvince}, ${locationState.country}`);
  }
  urlParams.set('destinationAddress.type', locationState?.destinationType || 'City');

  // Price and points range filters
  if (filterState?.priceRange?.min !== undefined || filterState?.priceRange?.max !== undefined) {
    const priceMin = filterState.priceRange.min ?? 0;
    const priceMax = filterState.priceRange.max ?? 9999;
    urlParams.set('price', `${priceMin}-${priceMax}`);
  }
  if (filterState?.pointsRange?.min !== undefined || filterState?.pointsRange?.max !== undefined) {
    const pointsMin = filterState.pointsRange.min ?? 0;
    const pointsMax = filterState.pointsRange.max ?? 999999;
    urlParams.set('points', `${pointsMin}-${pointsMax}`);
  }
  if (filterState?.distance !== undefined) {
    urlParams.set('distance', String(filterState.distance));
  }

  // States and cities filter parameters
  if (filterState?.states && filterState.states.length > 0) {
    urlParams.set('states', filterState.states.join(','));
  }
  if (filterState?.cities && filterState.cities.length > 0) {
    urlParams.set('cities', filterState.cities.join(','));
  }

  return urlParams.toString();
};

/**
 * Reads a cookie value from document.cookie by name.
 * Returns null if not found or if running server-side.
 */
const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split('; ');
  const cookie = cookies.find(c => c.startsWith(`${name}=`));
  return cookie ? cookie.substring(name.length + 1) : null;
};

/**
 * Builds the request body for the /validate/findHotels API call.
 * This follows the same structure as the shop component's interceptor response.
 */
const buildRequestBody = (queryString: string): Record<string, any> => {
  // Read cookie values for authentication context
  const altAccountIdCookie = getCookieValue('altAccountId');
  const rememberMeIdCodeTypeCookie = getCookieValue('rememberMeIdCodeType');
  const rememberMeIdCookie = getCookieValue('rememberMeId');
  const rememberMeCookie = getCookieValue('rememberMe');

  return {
    requestAttributes: {
      segmentId: null,
      geoLocationParams: {},
      deviceDetailsParams: {},
      globalParamObject: {
        englishSite: true,
        siteId: '',
        globalSite: false,
        country: 'US',
        language: 'en',
        siteName: 'marriott.com',
        domain: 'marriott.com',
        sitePath: '',
        mobileAuthEnabled: null,
        mobileDevice: false,
        domainFlag: '',
        bulgariFlag: false,
      },
      pageManagerRequest: {
        global: {
          localeKey: 'en_US',
          programFlag: '',
          domain: 'marriott.com',
          siteName: 'marriott.com',
        },
        pageContext: {
          queryString: encodeURIComponent(queryString),
        },
        locationParams: {},
        deviceParams: {},
      },
      altAccountIdCookie: altAccountIdCookie,
      rememberMeIdCodeTypeCookie: rememberMeIdCodeTypeCookie,
      rememberMeIdCookie: rememberMeIdCookie,
      rememberMeCookie: rememberMeCookie,
      travelAgentCookies: {
        megCookie: getCookieValue('meg'),
        hssMegCookie: getCookieValue('hssMeg'),
        hssCookie: getCookieValue('hss'),
        agentFNameCookie: getCookieValue('agentFName'),
        agentLNameCookie: getCookieValue('agentLName'),
        iataNumberCookie: getCookieValue('iataNumber'),
      },
      contextErrorDetails: [],
      isCookieValue: null,
      adobeSSId: getCookieValue('adobeSSId'),
      tntId: getCookieValue('tntId'),
      atSegmentID: getCookieValue('atSegmentID'),
      larContentNode: null,
      tnta: getCookieValue('tnta'),
    },
    requestingPath: `/search/findHotels?${queryString}`,
  };
};

/**
 * Checks if the location state has sufficient details to make a valid /validate/findHotels call.
 * Returns true if location has at least a destination, city, or coordinates.
 */
const hasValidLocationDetails = (locationState?: ConversationLocationState): boolean => {
  if (!locationState) return false;

  const hasDestination = !!(locationState.destination || locationState.city);
  const hasCoordinates = !!(locationState.latitude && locationState.longitude);
  const hasPlaceId = !!locationState.placeId;

  // Allow the call if we have any meaningful location info
  return hasDestination || hasCoordinates || hasPlaceId;
};

/**
 * Calls the /validate/findHotels API to update the session.
 * This is called at various touch points in the chat application.
 */
export const callValidateFindHotels = async (
  params: SessionUpdateParams,
  interceptorResponse?: any
): Promise<SessionUpdateResult> => {
  try {
    // currently not skipping the validate call if location details are not available
    if (!hasValidLocationDetails(params.locationState)) {
      console.warn(
        '[SessionUpdate] Insufficient location details - skipping /validate/findHotels call',
        params.locationState
      );
      // return {
      //   success: false,
      //   error: 'Location details not available - skipping validate call',
      // };
    }

    const envVars = useClientEnvVarsStore.getState().envVarsObject;
    const preProcessingUrl = envVars['NEXT_PUBLIC_PRE_PROCESSING_URL'];

    if (!preProcessingUrl) {
      return { success: false, error: 'NEXT_PUBLIC_PRE_PROCESSING_URL not configured' };
    }

    const queryParams = buildValidateFindHotelsQueryParams(params);
    const fullUrl = `${preProcessingUrl}?${queryParams}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Build the request body with requestAttributes
    // If interceptorResponse is provided (from shop context), use it
    // Otherwise, build a minimal requestAttributes object for chat context
    const requestBody = interceptorResponse || buildRequestBody(queryParams);

    const response = await axios.post(fullUrl, requestBody, { headers });

    // Extract revisionToken from the response and store it in Zustand
    const responseData = response?.data;
    console.log('[SessionUpdate] Full response data:', JSON.stringify(responseData, null, 2));

    // The sessionData might be at the root level or nested
    const sessionData = responseData?.sessionData || responseData;

    // Try multiple paths to find the revisionToken
    const revisionToken =
      sessionData?.data?.AriesCommon?.revisionToken ||
      sessionData?.cacheData?.data?.AriesCommon?.revisionToken ||
      responseData?.data?.AriesCommon?.revisionToken ||
      responseData?.AriesCommon?.revisionToken;

    console.log('[SessionUpdate] Extracted sessionData:', sessionData);
    console.log('[SessionUpdate] Extracted revisionToken:', revisionToken);

    if (revisionToken && typeof revisionToken === 'string') {
      const { setRevisionToken } = useSessionUpdateZustandStore.getState();
      setRevisionToken(revisionToken);
      console.log('[SessionUpdate] Stored revisionToken in Zustand:', revisionToken);
    } else {
      console.warn('[SessionUpdate] No valid revisionToken found in response');
    }

    return {
      success: true,
      sessionData: sessionData,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error?.message || 'Unknown error',
    };
  }
};

export type SessionUpdateReason = 'new_search_results' | 'mini_search_change' | 'filter_applied' | 'view_mode_change';

/**
 * Logs session update for debugging purposes.
 */
export const logSessionUpdate = (_reason: SessionUpdateReason, _params: SessionUpdateParams): void => {
  // Logging disabled
};
